import React, { useState, useEffect } from "react";
import {
    Card,
    Button,
    message,
    Alert,
    Row,
    Col,
    Statistic,
    Divider,
    Space,
    Steps,
    Typography,
    List,
    Tag,
    Progress,
    Tooltip,
} from "antd";
import {
    EyeOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    QuestionCircleOutlined,
    FileTextOutlined,
    ArrowLeftOutlined,
    BookOutlined,
    TrophyOutlined,
    RocketOutlined,
    EditOutlined,
    FileProtectOutlined,
    FileDoneOutlined,
    CheckCircleFilled,
    LockOutlined,
    PlayCircleOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "universal-cookie";
import { useLocalStorage } from "../hooks/useLocalStorage";
import LessonView from "./LessonView";

const { Paragraph, Text } = Typography;
const { Step } = Steps;

const CourseDetail = () => {
    const cookies = new Cookies();
    const { userData, updateUserData } = useLocalStorage();
    const auth = cookies.get("authToken");
    const config = { headers: { "auth-token": auth, "Content-Type": "multipart/form-data" } };
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [questionCount, setQuestionCount] = useState(0);
    const [userProgress, setUserProgress] = useState(null);
    const [lessons, setLessons] = useState([]);
    const [viewedLessonIds, setViewedLessonIds] = useState([]);
    const [selectedLesson, setSelectedLesson] = useState(null);

    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        fetchCourse();
    }, [id]);

    const fetchCourse = async () => {
        try {
            const response = await axios.get(`/api/courses/${id}`, config);
            setCourse(response.data);

            const questionsResponse = await axios.get(`/api/courses/${id}/questions`, config);
            setQuestionCount(questionsResponse.data.length);

            const progressResponse = await axios.get(`/api/user-courses/${id}`, config);
            setUserProgress(progressResponse.data);

            // ดึง lessons
            const lessonsResponse = await axios.get(`/api/courses/${id}/lessons`, config);
            setLessons(lessonsResponse.data);

            // ดึง lesson progress (ถ้า student ลงทะเบียนแล้ว)
            try {
                const lpRes = await axios.get(`/api/user-lesson-progress/${id}`, config);
                setViewedLessonIds(lpRes.data.viewed_lesson_ids || []);
            } catch {}

            setLoading(false);
        } catch (error) {
            message.error("ไม่สามารถดึงข้อมูลใบประกาศได้");
            setLoading(false);
        }
    };

    const handleLessonViewed = (lessonId, progressPercent) => {
        setViewedLessonIds((prev) => [...new Set([...prev, lessonId])]);
        if (userProgress) {
            setUserProgress((prev) => ({ ...prev, progress_percent: progressPercent }));
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!course) {
        return <div>Course not found</div>;
    }

    const renderEmbed = (link) => {
        // YouTube URL patterns
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})(.*)$/;
        const youtubeMatch = link.match(youtubeRegex);

        // Vimeo URL patterns
        const vimeoRegex = /^(https?:\/\/)?(www\.)?(vimeo\.com\/)([\d]+)(.*)$/;
        const vimeoMatch = link.match(vimeoRegex);

        let embedUrl = "";
        let linkType = "";

        if (youtubeMatch && youtubeMatch[4]) {
            linkType = "youtube";
            const videoId = youtubeMatch[4];
            embedUrl = `https://www.youtube.com/embed/${videoId}`;
        } else if (vimeoMatch && vimeoMatch[4]) {
            linkType = "vimeo";
            const videoId = vimeoMatch[4];
            embedUrl = `https://player.vimeo.com/video/${videoId}`;
        } else {
            linkType = "unknown";
            embedUrl = "";
        }

        if (linkType === "youtube" || linkType === "vimeo") {
            return (
                <div className="video-container">
                    <iframe
                        src={embedUrl}
                        width="560"
                        height="315"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Embedded Video"
                    ></iframe>
                </div>
            );
        } else {
            return (
                <div className="regular-link">
                    <a href={link} target="_blank" rel="noopener noreferrer">
                        {link}
                    </a>
                </div>
            );
        }
    };

    const getUserStatus = () => {
        if (!userProgress) {
            return "Not enrolled";
        }

        return userProgress.status || "enrolled";
    };

    const userStatus = getUserStatus();
    const hasCompletedCourse = userStatus === "passed";
    const isEnrolled = userStatus !== "Not enrolled";
    const hasAttemptedExam = userStatus === "failed" || userStatus === "passed";
    const allLessonsViewed = lessons.length === 0 || viewedLessonIds.length >= lessons.length;
    const isStudent = userData && userData.role === "student";

    // ถ้ากำลังดู lesson อยู่
    if (selectedLesson) {
        return (
            <>
                <Space align="baseline" style={{ marginBottom: 10 }}>
                    <ArrowLeftOutlined onClick={() => setSelectedLesson(null)} />
                    <h2>{course.title}</h2>
                </Space>
                <LessonView
                    lesson={selectedLesson}
                    lessons={lessons}
                    isViewed={viewedLessonIds.includes(selectedLesson.id)}
                    onViewed={handleLessonViewed}
                    onBack={() => setSelectedLesson(null)}
                    onPrev={() => {
                        const idx = lessons.findIndex((l) => l.id === selectedLesson.id);
                        if (idx > 0) setSelectedLesson(lessons[idx - 1]);
                    }}
                    onNext={() => {
                        const idx = lessons.findIndex((l) => l.id === selectedLesson.id);
                        if (idx < lessons.length - 1) setSelectedLesson(lessons[idx + 1]);
                    }}
                />
            </>
        );
    }

    return (
        <>
            <Space align="baseline" style={{ marginBottom: "10px" }}>
                <ArrowLeftOutlined onClick={() => navigate(-1)} />
                <h2>ใบประกาศ</h2>
            </Space>
            {userProgress && userProgress.enrollment_date && (
                <>
                    <Card title="ความคืบหน้า">
                        <div>
                            <Steps
                                direction="vertical"
                                current={
                                    userStatus === "passed"
                                        ? 2
                                        : userStatus === "failed"
                                          ? 1
                                          : userStatus === "in_progress"
                                            ? 1
                                            : userStatus === "enrolled"
                                              ? 0
                                              : -1
                                }
                            >
                                <Step
                                    title="Enrolled"
                                    description={
                                        userProgress.enrollment_date
                                            ? `Enrolled on ${new Date(userProgress.enrollment_date).toLocaleDateString(
                                                  "th-TH",
                                                  {
                                                      day: "2-digit",
                                                      month: "2-digit",
                                                      year: "numeric",
                                                  },
                                              )}`
                                            : null
                                    }
                                    icon={<BookOutlined />}
                                />
                                <Step
                                    title="In Progress"
                                    description={
                                        userProgress.last_accessed
                                            ? `Last accessed on ${new Date(
                                                  userProgress.last_accessed,
                                              ).toLocaleDateString("th-TH", {
                                                  day: "2-digit",
                                                  month: "2-digit",
                                                  year: "numeric",
                                              })}`
                                            : null
                                    }
                                    icon={<RocketOutlined />}
                                />
                                <Step
                                    title="Qualified"
                                    description={
                                        userProgress.completion_date
                                            ? `Qualified on ${new Date(userProgress.completion_date).toLocaleDateString(
                                                  "th-TH",
                                                  {
                                                      day: "2-digit",
                                                      month: "2-digit",
                                                      year: "numeric",
                                                  },
                                              )}`
                                            : null
                                    }
                                    icon={<FileDoneOutlined />}
                                />
                            </Steps>
                        </div>
                        {hasCompletedCourse && (
                            <Alert
                                message="Examination Qualified"
                                description={
                                    <div>
                                        <Paragraph>You have successfully passed this course's examination!</Paragraph>
                                        <Button type="primary" onClick={() => navigate("/certificates")}>
                                            View Your Certificate
                                        </Button>
                                    </div>
                                }
                                type="success"
                                showIcon
                                icon={<FileProtectOutlined />}
                            />
                        )}
                    </Card>
                    <Divider></Divider>
                </>
            )}
            <Card
                title={course.title}
                extra={
                    <Space>
                        {userData && userData.role !== "student" && (
                            <Button
                                type="primary"
                                onClick={() => navigate(`/course/edit/${id}`)}
                                icon={<EditOutlined />}
                            >
                                แก้ไขใบประกาศ
                            </Button>
                        )}
                        {!hasCompletedCourse && isStudent && questionCount > 0 && (
                            <Tooltip
                                title={
                                    !allLessonsViewed
                                        ? `เรียนให้ครบก่อน (${viewedLessonIds.length}/${lessons.length} บท)`
                                        : ""
                                }
                            >
                                <Button
                                    type="primary"
                                    onClick={() => navigate(`/course/${id}/exam`)}
                                    icon={allLessonsViewed ? <FileTextOutlined /> : <LockOutlined />}
                                    disabled={!allLessonsViewed}
                                >
                                    เริ่มทำการสอบ
                                </Button>
                            </Tooltip>
                        )}
                        <Statistic value={course.view_count || 0} prefix={<EyeOutlined />} />
                    </Space>
                }
            >
                <Card title="รายละเอียด">
                    <div dangerouslySetInnerHTML={{ __html: course.description }} />
                </Card>

                {/* Lesson List */}
                {lessons.length > 0 && (
                    <Card
                        title={
                            <Space>
                                <BookOutlined />
                                <span>บทเรียน</span>
                                <Tag color={allLessonsViewed ? "success" : "processing"}>
                                    {viewedLessonIds.length}/{lessons.length} บท
                                </Tag>
                            </Space>
                        }
                        style={{ marginTop: 16 }}
                        extra={
                            isStudent && (
                                <Progress
                                    percent={Math.round((viewedLessonIds.length / lessons.length) * 100)}
                                    size="small"
                                    style={{ width: 120 }}
                                    status={allLessonsViewed ? "success" : "active"}
                                />
                            )
                        }
                    >
                        <List
                            dataSource={lessons}
                            renderItem={(lesson, index) => {
                                const isLessonViewed = viewedLessonIds.includes(lesson.id);
                                return (
                                    <List.Item
                                        key={lesson.id}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => isStudent && setSelectedLesson(lesson)}
                                        actions={
                                            isStudent
                                                ? [
                                                      isLessonViewed ? (
                                                          <Tag
                                                              icon={<CheckCircleFilled />}
                                                              color="success"
                                                              key="viewed"
                                                          >
                                                              เรียนแล้ว
                                                          </Tag>
                                                      ) : (
                                                          <Tag color="default" key="not-viewed">
                                                              ยังไม่ได้เรียน
                                                          </Tag>
                                                      ),
                                                  ]
                                                : []
                                        }
                                    >
                                        <List.Item.Meta
                                            avatar={
                                                <div
                                                    style={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: "50%",
                                                        background: isLessonViewed ? "#52c41a" : "#1677ff",
                                                        color: "#fff",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    {isLessonViewed ? <CheckCircleFilled /> : index + 1}
                                                </div>
                                            }
                                            title={
                                                <Space>
                                                    <Text>{lesson.title}</Text>
                                                    {isStudent && !isLessonViewed && (
                                                        <PlayCircleOutlined style={{ color: "#1677ff" }} />
                                                    )}
                                                </Space>
                                            }
                                            description={lesson.description}
                                        />
                                    </List.Item>
                                );
                            }}
                        />
                    </Card>
                )}

                {/* {course && course.video_path && (
                    <Card title="วิดีโอ" style={{ marginTop: 16 }}>
                        <video controls style={{ width: "100%", maxWidth: "640px" }} className="mb-2">
                            <source src={`/${course.video_path}`} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </Card>
                )} */}

                {course && course.links.length > 0 && (
                    <Card title="ลิงค์ภายนอก" style={{ marginTop: 16 }}>
                        {/* checkLinkType(course); */}
                        {course.links.map((link) => (
                            <Card title={link.title} style={{ marginBottom: 16 }}>
                                {renderEmbed(link.url)}
                            </Card>
                        ))}
                    </Card>
                )}

                {/* Exam Information */}
                {!hasCompletedCourse && (
                    <Card title="ข้อมูลการสอบ" style={{ marginTop: 16 }}>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Statistic
                                    title="เวลาในการสอบ"
                                    value={`${course.exam_time_limit || 30} นาที`}
                                    prefix={<ClockCircleOutlined />}
                                />
                            </Col>
                            <Col span={8}>
                                <Statistic
                                    title="เกณฑ์ผ่าน"
                                    value={
                                        course.exam_pass_threshold_type === "percentage"
                                            ? `${course.exam_pass_threshold}%`
                                            : `${course.exam_pass_threshold} ข้อ`
                                    }
                                    prefix={<CheckCircleOutlined />}
                                />
                            </Col>
                            <Col span={8}>
                                <Statistic
                                    title="จำนวนข้อสอบทั้งหมด"
                                    value={questionCount}
                                    prefix={<QuestionCircleOutlined />}
                                />
                            </Col>
                        </Row>

                        {questionCount > 0 ? (
                            <div style={{ marginTop: 16, textAlign: "center" }}>
                                {isStudent && (
                                    <Tooltip
                                        title={
                                            !allLessonsViewed
                                                ? `เรียนให้ครบก่อน (${viewedLessonIds.length}/${lessons.length} บท)`
                                                : ""
                                        }
                                    >
                                        <Button
                                            type="primary"
                                            size="large"
                                            onClick={() => navigate(`/course/${id}/exam`)}
                                            icon={allLessonsViewed ? <FileTextOutlined /> : <LockOutlined />}
                                            disabled={!allLessonsViewed}
                                        >
                                            เริ่มทำการสอบ
                                        </Button>
                                    </Tooltip>
                                )}
                            </div>
                        ) : (
                            <div style={{ marginTop: 16, textAlign: "center" }}>No exam questions available</div>
                        )}
                    </Card>
                )}
            </Card>
        </>
    );
};

export default CourseDetail;
